import { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/Base/Badge";
import { HeaderSortable } from "@/components/Base/HeaderSortable";
import { ShopAuditRow } from "@/hooks/queries/useShopConfigAuditRows";
import { shopCreatedDate } from "@/lib/shopAuditList";
import { ListAuditResult, SettingsAuditResult } from "@/lib/shopConfigAudit";
import { countStatuses } from "@/lib/shopConfigAuditView";

const PILL = "px-2 font-bold whitespace-nowrap";

const SettingsCell = ({ result }: { result: SettingsAuditResult }) => {
  const { missing, drift } = countStatuses(result.rows);
  if (missing === 0 && drift === 0) {
    return (
      <Badge variant="success" className={PILL}>
        OK
      </Badge>
    );
  }
  return (
    <div className="flex flex-wrap gap-1">
      {missing > 0 && (
        <Badge variant="destructive" className={PILL}>
          {missing} not set
        </Badge>
      )}
      {drift > 0 && (
        <Badge variant="warning" className={PILL}>
          {drift} drifted
        </Badge>
      )}
    </div>
  );
};

const ListCell = ({
  result,
  error,
}: {
  result: ListAuditResult;
  error?: string;
}) =>
  error ? (
    <Badge variant="destructive" className={PILL} title={error}>
      Error
    </Badge>
  ) : (
    <Badge
      variant={result.missing.length > 0 ? "destructive" : "success"}
      className={PILL}
    >
      {result.present.length}/{result.expectedCount}
    </Badge>
  );

interface ColumnProps {
  onSelectRow: (row: ShopAuditRow) => void;
}

export const shopConfigAuditColumns = ({
  onSelectRow,
}: ColumnProps): ColumnDef<ShopAuditRow>[] => [
  {
    // Column ids are the sort keys the table's URL state uses (see
    // ShopSortKey in shopAuditList.ts).
    id: "name",
    accessorFn: (row) => row.shop.name,
    header: ({ column }) => (
      <HeaderSortable column={column} title="Shop Name" />
    ),
    cell: ({ row }) => row.original.shop.name,
  },
  {
    id: "createdAt",
    accessorFn: (row) => shopCreatedDate(row.shop),
    header: ({ column }) => <HeaderSortable column={column} title="Created" />,
    cell: ({ row }) => (
      <span className="whitespace-nowrap">
        {shopCreatedDate(row.original.shop)?.toLocaleDateString() ?? "—"}
      </span>
    ),
  },
  {
    id: "shopConfig",
    header: "Config Defaults",
    cell: ({ row }) => <SettingsCell result={row.original.shopConfig} />,
  },
  {
    id: "cardColors",
    header: "Card Colors",
    cell: ({ row }) => <SettingsCell result={row.original.cardColors} />,
  },
  {
    id: "enabledFeatures",
    header: "Feature Flags",
    cell: ({ row }) => <SettingsCell result={row.original.enabledFeatures} />,
  },
  {
    id: "tags",
    header: "Tags",
    cell: ({ row }) => (
      <ListCell result={row.original.tags} error={row.original.fetchError} />
    ),
  },
  {
    id: "automations",
    header: "Automations",
    cell: ({ row }) => (
      <ListCell
        result={row.original.automations}
        error={row.original.fetchError}
      />
    ),
  },
  {
    id: "actions",
    header: "",
    cell: ({ row }) => (
      // On the dark shell: blue-300 (text-primary is blue-800, ~2:1 on slate-900).
      <button
        type="button"
        className="text-[13px] text-blue-300 underline hover:text-blue-200"
        onClick={(e) => {
          // The row's own onRowClick already selects it; stop the bubble so
          // the selection callback fires once.
          e.stopPropagation();
          onSelectRow(row.original);
        }}
      >
        View details
      </button>
    ),
  },
];
